import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

const ADMIN_PROBE_EMAIL = 'preview@travpromobile.com';

interface ImpersonationErrorResponse {
  ok: false;
  response: Response;
}

function isImpersonationError(res: unknown): res is ImpersonationErrorResponse {
  return typeof res === 'object' && res !== null && (res as { ok?: unknown }).ok === false;
}

/**
 * Probes the impersonation endpoint with a fixed test email — a non-error response means the
 * current user is an admin. This is a client-side UI gate only; the endpoint enforces the real
 * authorization server-side (a non-admin gets a 403, surfaced here as `isImpersonationError`).
 *
 * While actively impersonating, the "current" accessToken belongs to the target (non-admin) user,
 * so the probe must run against the backed-up original admin token instead — otherwise an admin
 * who's impersonating would get redirected away from /impersonate before they could end it.
 */
export function useImpersonationAdminCheck(enabled = true): UseQueryResult<boolean> {
  return useQuery<boolean>({
    queryKey: ['impersonation-admin'],
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<boolean> => {
      const originalAccessToken = (await StorageHandler.load(storageKeys.impersonationOriginalAccessToken)) as
        | string
        | false;
      const accessToken =
        originalAccessToken || ((await StorageHandler.load(storageKeys.accessToken)) as string | false);
      if (!accessToken) return false;

      const res = (await APIHandler.getImpersonationToken(ADMIN_PROBE_EMAIL, accessToken)) as unknown;
      return !isImpersonationError(res) && res !== false;
    },
  });
}
